import { Back, TweenMax } from 'gsap';
import { GameAudio } from '../../../../Config/Audio';
import { getGameSize } from '../../../../Config/Levels';
import { ShareableMoments } from '../../../../SDK/SDKmessages';
import SoundManager from '../../../../Sound/SoundManager';
import { createOutlineText, getRandomFloat } from '../../../../Utils/Utils';
import { Animation } from '../../../Animations/Animation';
import { FallingParticle } from '../../../Animations/FallingParticle';
import { HighlightEffect } from '../../../Shared/HighlightEffect';
import { ShareableOverlay } from './ShareableOverlay';
import Core from '../../../../Core/Core';

export class JackpotShareableOverlay extends ShareableOverlay {
    private readonly coinGroup: PIXI.Container;
    private readonly characterGroup: PIXI.Sprite;
    private character: PIXI.Sprite;
    private readonly jong: PIXI.Sprite;
    private readonly flashLight: PIXI.Sprite;
    private readonly coinParticle: FallingParticle;
    private readonly tweenInAnimation: Animation;

    constructor() {
        super(ShareableMoments.JACKPOT);
        const { gameWidth, gameHeight } = getGameSize();
        const highlight = new HighlightEffect({ scale: 1.2 });
        highlight.x = gameWidth / 2;
        highlight.y = gameHeight * 0.45;
        highlight.appear();
        const coinSprite = new PIXI.Sprite(
            PIXI.utils.TextureCache['commonui/leaderboard-reward-coins']
        );
        coinSprite.scale.set(1.5);
        coinSprite.position = highlight.position;

        const textDisplay = createOutlineText(
            `1000 ${Core.local.getText('coins')}!`,
            new PIXI.Rectangle(
                coinSprite.x,
                coinSprite.y + coinSprite.height * 0.5,
                coinSprite.width * 2,
                coinSprite.height * 0.3
            ),
            'Baloo',
            30,
            21,
            0xffffff,
            0x2a3a71,
            4
        );
        textDisplay.anchor.set(0.5, 0);

        this.coinGroup = new PIXI.Container();
        this.coinGroup.addChild(highlight, coinSprite, textDisplay);
        coinSprite.anchor.set(0.5);

        const character = (this.character = new PIXI.Sprite(
            PIXI.utils.TextureCache['character/Jackpot_Character']
        ));
        character.anchor.set(0.5);

        const jong = (this.jong = new PIXI.Sprite(
            PIXI.utils.TextureCache['character/Jackpot_pet']
        ));
        jong.anchor.set(0.5);
        jong.x = gameWidth / 2 + jong.width / 1.2;
        jong.y = gameHeight + jong.height / 2;

        const flashLight = (this.flashLight = new PIXI.Sprite(
            PIXI.utils.TextureCache['fx/Jackpot_flash']
        ));
        flashLight.anchor.set(0.5);
        flashLight.alpha = 0;
        flashLight.x = character.width * 0.25;
        flashLight.y = -character.height * 0.25;

        const characterGroup = (this.characterGroup = new PIXI.Sprite());
        characterGroup.addChild(flashLight, character);
        characterGroup.x = -character.width;
        characterGroup.y = gameHeight;

        this.coinParticle = new FallingParticle(
            () => {
                const coin = new PIXI.Sprite(PIXI.utils.TextureCache['commonui/reward-coin01']);
                coin.anchor.set(0.5);
                coin.scale.set(getRandomFloat(0.5, 0.7));
                return coin;
            },
            false,
            100,
            [2, 7]
        );
        this.tweenInAnimation = this.createTweenInAnimation();

        this.addChildAt(this.characterGroup, 0);
        this.addChildAt(this.jong, 0);
        this.addChildAt(this.coinGroup, 0);
        this.addChildAt(this.coinParticle, 0);
    }

    public async tweenIn(): Promise<void> {
        SoundManager.playSound(GameAudio.PRIZEWHEEL_REWARD);
        this.tweenInAnimation.play();
    }

    public async tweenOut(): Promise<void> {
        SoundManager.playSound(GameAudio.PRIZEWHEEL_REWARD);
        this.tweenInAnimation.timeScale = 5;
        await this.tweenInAnimation.reversed();
        this.destroy();
    }

    private createTweenInAnimation() {
        const { gameWidth, gameHeight } = getGameSize();
        const animation = new Animation({});
        animation.add(() => this.coinParticle.createFallingParticles());
        animation.addToLast(
            TweenMax.to(this.coinGroup.scale, 0.4, {
                x: 1,
                y: 1,
                ease: Back.easeOut.config(3),
            })
        );
        animation.addToLast(
            TweenMax.to(this.jong, 1, {
                y: gameHeight - this.jong.height,
                ease: Back.easeOut.config(1),
            })
        );
        animation.addToLast(
            TweenMax.to(this.characterGroup, 1, {
                delay: 0.5,
                x: gameWidth / 2 - this.character.width / 1.8,
                y: this.characterGroup.y - this.character.height / 2.3,
                ease: Back.easeOut.config(1),
            })
        );
        animation.addToLast(
            TweenMax.to(this.flashLight, 0.3, {
                delay: 0.5,
                alpha: 1,
                repeat: 2,
                repeatDelay: 0.33,
            })
        );
        animation.addToLast(
            TweenMax.to(this.flashLight, 0.3, {
                delay: 0.8,
                alpha: 0,
                repeat: 2,
                repeatDelay: 0.33,
            })
        );

        animation.add(() => super.tweenIn());
        return animation;
    }
}
